// Complete uploads through the same adapter as ordinary Send before moving a
// draft to the waiting store. A file and its text always travel together.
import type {
  AttachmentAdapter,
  CompleteAttachment,
  ThreadComposerRuntime,
} from '@assistant-ui/react';
import type { SteerActions } from './actions';

export function createSteerComposer(
  composer: ThreadComposerRuntime,
  adapter: AttachmentAdapter,
  actions: SteerActions,
) {
  let preparing = false;
  return async (now = false): Promise<void> => {
    if (preparing) return;
    const draft = composer.getState();
    if (!draft.text.trim() && !draft.attachments.length) return;
    if (
      draft.attachments.some((a) => a.status.type === 'running' || a.status.type === 'incomplete')
    )
      return;
    preparing = true;
    try {
      const attachments = await Promise.all(
        draft.attachments.map(async (attachment) => {
          const ready =
            attachment.status.type === 'complete'
              ? (attachment as CompleteAttachment)
              : await adapter.send(attachment as Parameters<AttachmentAdapter['send']>[0]);
          // File objects cannot survive sessionStorage. The completed content is
          // sufficient for native append and for restoring an editable attachment.
          const { file: _file, ...saved } = ready;
          return saved;
        }),
      );
      const current = composer.getState();
      // Edits/removals during preparation belong to the person, not this send.
      if (
        current.text !== draft.text ||
        current.attachments.length !== draft.attachments.length ||
        current.attachments.some((attachment, i) => attachment !== draft.attachments[i])
      )
        return;
      composer.setText('');
      await composer.clearAttachments();
      if (now) await actions.sendNow(draft.text, attachments);
      else await actions.submit(draft.text, attachments);
    } finally {
      preparing = false;
    }
  };
}
